import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const propertyTypes = ['Appartamento', 'Villa', 'Mansarda', 'Terreno', 'Locale Commerciale', 'Ufficio', 'Box Auto', 'Posto Auto', 'Soluzione Semi-Indipendente', 'Soluzione Indipendente'];

const benefits = [
  'Valutazione gratuita e senza impegno',
  'Promozione su tutti i portali immobiliari',
  'Servizio fotografico professionale',
  'Assistenza legale inclusa',
  'Nessun costo anticipato',
];

export default function SellPage() {
  const [form, setForm] = useState({
    owner_name: '',
    email: '',
    phone: '',
    property_type: '',
    city: '',
    address: '',
    area_sqm: '',
    rooms: '',
    description: '',
    asking_price: '',
    privacy: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.privacy) {
      toast.error('Devi accettare la Privacy Policy per continuare');
      return;
    }
    setSubmitting(true);
    try {
      const { privacy, ...payload } = form;
      await axios.post(`${API}/sell-requests`, {
        ...payload,
        area_sqm: parseFloat(form.area_sqm) || 0,
        rooms: parseInt(form.rooms) || null,
        asking_price: parseFloat(form.asking_price) || null,
      });
      setSubmitted(true);
      toast.success('Richiesta inviata con successo!');
    } catch (error) {
      toast.error('Errore nell\'invio della richiesta');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="sell-page-success" className="min-h-screen pt-32 pb-24 flex items-center justify-center">
        <div className="text-center max-w-lg mx-auto px-4">
          <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={40} className="text-primary" />
          </div>
          <h1 className="font-serif text-4xl text-text-main mb-4">Richiesta Inviata!</h1>
          <p className="font-sans text-text-muted mb-8">
            Grazie per averci contattato. Un nostro consulente ti ricontatterà entro 24 ore per fissare un appuntamento.
          </p>
          <Button onClick={() => setSubmitted(false)} variant="outline">
            Invia un'altra richiesta
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="sell-page">
      {/* Hero */}
      <section className="relative pt-32 pb-24 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl animate-fadeIn">
            <p className="text-xs font-sans uppercase tracking-[0.2em] font-medium text-accent mb-4">
              Vendi con Noi
            </p>
            <h1 className="font-serif text-5xl md:text-6xl text-white tracking-tight leading-[1.1] mb-6">
              Vendi la tua casa al miglior prezzo
            </h1>
            <p className="font-sans text-lg text-white/80 leading-relaxed">
              Affidati alla nostra esperienza per vendere il tuo immobile in tempi rapidi 
              e al prezzo giusto. Valutazione gratuita e senza impegno.
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Benefits */}
            <div>
              <h2 className="font-serif text-3xl md:text-4xl text-text-main mb-8">
                Perché vendere con Green House?
              </h2>
              <div className="space-y-4 mb-8">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3 animate-fadeIn" style={{ animationDelay: `${index * 0.1}s` }}>
                    <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle size={14} className="text-primary" />
                    </div>
                    <span className="font-sans text-text-main">{benefit}</span>
                  </div>
                ))}
              </div>

              <div className="bg-background-alt p-8 rounded-xl">
                <h3 className="font-serif text-xl text-text-main mb-4">Come funziona?</h3>
                <div className="space-y-4">
                  {[
                    { step: '1', title: 'Compila il form', desc: 'Inserisci i dati del tuo immobile' },
                    { step: '2', title: 'Ti ricontattiamo', desc: 'Un consulente ti chiamerà entro 24h' },
                    { step: '3', title: 'Valutazione gratuita', desc: 'Visitiamo l\'immobile e forniamo una stima' },
                    { step: '4', title: 'Vendita', desc: 'Gestiamo tutto noi fino al rogito' },
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-4">
                      <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center flex-shrink-0 font-sans font-medium text-sm">
                        {item.step}
                      </div>
                      <div>
                        <h4 className="font-sans font-medium text-text-main">{item.title}</h4>
                        <p className="font-sans text-sm text-text-muted">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="bg-white p-8 rounded-xl shadow-lg" data-testid="sell-form">
              <h3 className="font-serif text-2xl text-text-main mb-6">Richiedi una valutazione</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <Input
                    placeholder="Nome e Cognome *"
                    value={form.owner_name}
                    onChange={(e) => setForm({ ...form, owner_name: e.target.value })}
                    required
                    data-testid="sell-owner-name"
                  />
                  <Input
                    type="email"
                    placeholder="Email *"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    required
                    data-testid="sell-email"
                  />
                </div>
                <Input
                  type="tel"
                  placeholder="Telefono *"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  required
                  data-testid="sell-phone"
                />
                <Select
                  value={form.property_type}
                  onValueChange={(value) => setForm({ ...form, property_type: value })}
                >
                  <SelectTrigger data-testid="sell-property-type">
                    <SelectValue placeholder="Tipo di immobile *" />
                  </SelectTrigger>
                  <SelectContent>
                    {propertyTypes.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="grid md:grid-cols-2 gap-4">
                  <Input
                    placeholder="Città *"
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    required
                    data-testid="sell-city"
                  />
                  <Input
                    placeholder="Indirizzo *"
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    required
                    data-testid="sell-address"
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <Input
                    type="number"
                    placeholder="Superficie (m²) *"
                    value={form.area_sqm}
                    onChange={(e) => setForm({ ...form, area_sqm: e.target.value })}
                    required
                    data-testid="sell-area"
                  />
                  <Input
                    type="number"
                    placeholder="Numero locali"
                    value={form.rooms}
                    onChange={(e) => setForm({ ...form, rooms: e.target.value })}
                    data-testid="sell-rooms"
                  />
                </div>
                <Input
                  type="number"
                  placeholder="Prezzo richiesto (€)"
                  value={form.asking_price}
                  onChange={(e) => setForm({ ...form, asking_price: e.target.value })}
                  data-testid="sell-price"
                />
                <Textarea
                  placeholder="Descrizione dell'immobile (facoltativa)"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={4}
                  data-testid="sell-description"
                />
                <label className="flex items-start gap-2 text-sm font-sans text-text-muted cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.privacy}
                    onChange={(e) => setForm({ ...form, privacy: e.target.checked })}
                    required
                    className="mt-0.5 w-4 h-4 accent-primary flex-shrink-0"
                    data-testid="sell-privacy"
                  />
                  <span>
                    Dichiaro di aver letto e accetto la{' '}
                    <a href="https://www.iubenda.com/privacy-policy/84083459" className="text-primary underline hover:text-primary-hover" target="_blank" rel="noopener noreferrer">
                      Privacy Policy
                    </a>
                    {' '}(GDPR) *
                  </span>
                </label>
                <Button
                  type="submit"
                  className="w-full bg-primary hover:bg-primary-hover"
                  disabled={submitting}
                  data-testid="sell-submit"
                >
                  {submitting ? 'Invio in corso...' : 'Richiedi Valutazione Gratuita'}
                  <ArrowRight size={18} className="ml-2" />
                </Button>
                <p className="font-sans text-xs text-text-muted text-center">
                  * Campi obbligatori. I tuoi dati saranno trattati nel rispetto della privacy.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
